import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where
} from 'firebase/firestore';
import { Dialog } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/firebase';
import VisorRA from '../VisorRA';
import { describeGeoError } from './geoLocationError';
import './geoScenes.css';
import { loadGoogleMaps } from './googleMaps';

const DEFAULT_CENTER = [-29.413435, -66.85677];
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const PRECISE_CAPTURE_SECONDS = 30;
const PRECISE_GOOD_ACCURACY_METERS = 20;
const PRECISE_TARGET_ACCURACY_METERS = 8;

const parseUniformScale = (scaleAttr) => {
  if (typeof scaleAttr !== 'string') return null;
  const parts = scaleAttr.trim().split(/\s+/).map(Number);
  if (parts.length === 0 || !Number.isFinite(parts[0])) return null;
  return parts[0];
};

const parseYawFromOrientation = (orientationAttr) => {
  if (typeof orientationAttr !== 'string') return null;
  const parts = orientationAttr.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const v = Number(parts[1].replace('deg', ''));
  return Number.isFinite(v) ? v : null;
};

const sanitizeArTransform = (transform) => {
  if (!transform || typeof transform !== 'object') return null;
  const safe = {};
  if (typeof transform.position === 'string' && transform.position.trim()) {
    safe.position = transform.position;
  }
  if (typeof transform.orientation === 'string' && transform.orientation.trim()) {
    safe.orientation = transform.orientation;
  }
  if (typeof transform.scale === 'string' && transform.scale.trim()) {
    safe.scale = transform.scale;
  }
  return Object.keys(safe).length > 0 ? safe : null;
};

const buildFallbackTransform = (scaleValue, rotationYValue) => {
  const s = Number.isFinite(Number(scaleValue)) && Number(scaleValue) > 0 ? Number(scaleValue) : 1;
  const r = Number.isFinite(Number(rotationYValue)) ? Number(rotationYValue) : 0;
  return {
    position: '0 0 0',
    orientation: `0deg ${r}deg 0deg`,
    scale: `${s} ${s} ${s}`
  };
};

function GeoPointPicker({ value, onPick, height = 260 }) {
  const holderRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const clickListenerRef = useRef(null);
  const onPickRef = useRef(onPick);
  const [error, setError] = useState(null);

  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    let mounted = true;
    if (!GOOGLE_MAPS_API_KEY) {
      setError('Falta configurar REACT_APP_GOOGLE_MAPS_API_KEY para usar Google Maps.');
      return () => {};
    }
    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then((maps) => {
        if (!mounted || !holderRef.current) return;
        const center = {
          lat: Number(value?.[0] ?? DEFAULT_CENTER[0]),
          lng: Number(value?.[1] ?? DEFAULT_CENTER[1])
        };
        if (!mapRef.current) {
          mapRef.current = new maps.Map(holderRef.current, {
            center,
            zoom: 16,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false
          });
          clickListenerRef.current = mapRef.current.addListener('click', (ev) => {
            const lat = ev.latLng?.lat();
            const lng = ev.latLng?.lng();
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
            if (!markerRef.current) {
              markerRef.current = new maps.Marker({ map: mapRef.current });
            }
            markerRef.current.setPosition({ lat, lng });
            onPickRef.current?.({ lat, lng });
          });
        }

        mapRef.current.setCenter(center);
        if (value) {
          if (!markerRef.current) markerRef.current = new maps.Marker({ map: mapRef.current });
          markerRef.current.setPosition(center);
        } else if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
      })
      .catch((e) => {
        console.error(e);
        if (mounted) setError('No se pudo cargar Google Maps.');
      });

    return () => {
      mounted = false;
    };
  }, [value]);

  useEffect(() => {
    return () => {
      if (clickListenerRef.current && window.google?.maps?.event) {
        window.google.maps.event.removeListener(clickListenerRef.current);
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, []);

  if (error) return <div className="geo-street-error">{error}</div>;
  return <div ref={holderRef} className="geo-point-map" style={{ height }} />;
}

function StreetViewPicker({ value, onPick, height = 320, apiKey }) {
  const holderRef = useRef(null);
  const panoRef = useRef(null);
  const onPickRef = useRef(onPick);
  const initialValueRef = useRef(value);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    let mounted = true;
    if (!apiKey) {
      setError('Falta configurar REACT_APP_GOOGLE_MAPS_API_KEY para usar Street View integrado.');
      return () => {};
    }
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (!mounted || !holderRef.current) return;
        const initialLat = Number(initialValueRef.current?.[0] ?? DEFAULT_CENTER[0]);
        const initialLng = Number(initialValueRef.current?.[1] ?? DEFAULT_CENTER[1]);
        const pano = new maps.StreetViewPanorama(holderRef.current, {
          position: { lat: initialLat, lng: initialLng },
          pov: { heading: 34, pitch: 8 },
          addressControl: false,
          fullscreenControl: false,
          motionTracking: false,
          enableCloseButton: false
        });
        pano.addListener('position_changed', () => {
          const pos = pano.getPosition();
          if (!pos) return;
          onPickRef.current?.({ lat: pos.lat(), lng: pos.lng() });
        });
        panoRef.current = pano;
        setReady(true);
      })
      .catch((e) => {
        console.error(e);
        if (mounted) setError('No se pudo cargar Street View. Revisa la API key de Google Maps.');
      });

    return () => {
      mounted = false;
      if (panoRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(panoRef.current);
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (!ready || !panoRef.current || !value) return;
    const current = panoRef.current.getPosition();
    const lat = Number(value[0]);
    const lng = Number(value[1]);
    if (!current) {
      panoRef.current.setPosition({ lat, lng });
      return;
    }
    const dLat = Math.abs(current.lat() - lat);
    const dLng = Math.abs(current.lng() - lng);
    if (dLat > 0.00002 || dLng > 0.00002) {
      panoRef.current.setPosition({ lat, lng });
    }
  }, [ready, value]);

  if (error) {
    return <div className="geo-street-error">{error}</div>;
  }

  return <div ref={holderRef} className="geo-street-view" style={{ height }} />;
}

const emptySceneForm = {
  id: null,
  name: '',
  description: '',
  location: '',
  isPublic: false,
  center: null,
  activationRadiusMeters: 120,
  autoOpenOnEnter: false,
  routeGuided: false,
  routeAutoAdvance: true
};

const emptyPlacementForm = {
  id: null,
  modelId: '',
  title: '',
  description: '',
  lat: '',
  lng: '',
  scale: 1,
  rotationY: 0,
  arTransform: null,
  activationRadiusMeters: 25,
  routeOrder: 1
};

export default function GeoScenesAdmin() {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [scenes, setScenes] = useState([]);
  const [models, setModels] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [sceneForm, setSceneForm] = useState(emptySceneForm);
  const [placementForm, setPlacementForm] = useState(emptyPlacementForm);
  const [loading, setLoading] = useState(true);
  const [savingScene, setSavingScene] = useState(false);
  const [savingPlacement, setSavingPlacement] = useState(false);
  const [creatingNewScene, setCreatingNewScene] = useState(false);
  const [sceneFormOpen, setSceneFormOpen] = useState(false);
  const [placementStep, setPlacementStep] = useState(1);
  const [locationTab, setLocationTab] = useState('map');
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [calibrationArStatus, setCalibrationArStatus] = useState('idle');
  const [calibrationTransform, setCalibrationTransform] = useState(null);
  const [calibrationScale, setCalibrationScale] = useState(1);
  const [calibrationRotationY, setCalibrationRotationY] = useState(0);
  const [calibrationSaveState, setCalibrationSaveState] = useState('idle');
  const [calibrationSaveErrorMessage, setCalibrationSaveErrorMessage] = useState('');
  const [capturingGpsPoint, setCapturingGpsPoint] = useState(false);
  const [gpsPointInfo, setGpsPointInfo] = useState(null);
  const [gpsCaptureStats, setGpsCaptureStats] = useState(null);
  const gpsWatchIdRef = useRef(null);
  const gpsTimerRef = useRef(null);
  const gpsSamplesRef = useRef([]);
  const gpsCaptureStartRef = useRef(null);

  const selectedModel = useMemo(
    () => models.find((m) => m.id === placementForm.modelId) || null,
    [models, placementForm.modelId]
  );

  const loadScenes = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      let snap;
      try {
        snap = await getDocs(query(
          collection(db, 'geoScenes'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        ));
      } catch {
        snap = await getDocs(query(
          collection(db, 'geoScenes'),
          where('userId', '==', currentUser.uid)
        ));
      }

      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setScenes(list);
      if (!sceneForm.id && list.length > 0 && !creatingNewScene) {
        const first = list[0];
        setSceneForm({
          id: first.id,
          name: first.name || '',
          description: first.description || '',
          location: first.location || '',
          isPublic: !!first.isPublic,
          center: Array.isArray(first.center) ? first.center : null,
          activationRadiusMeters: Number(first.activationRadiusMeters) || 120,
          autoOpenOnEnter: !!first.autoOpenOnEnter,
          routeGuided: !!first.routeGuided,
          routeAutoAdvance: typeof first.routeAutoAdvance === 'boolean' ? first.routeAutoAdvance : true
        });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudieron cargar las escenas geograficas', { variant: 'error' });
    }
  }, [currentUser?.uid, enqueueSnackbar, sceneForm.id, creatingNewScene]);

  const loadModels = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const snap = await getDocs(query(
        collection(db, 'models'),
        where('userId', '==', currentUser.uid)
      ));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((m) => !!m.modelUrl);
      setModels(list);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudieron cargar tus modelos', { variant: 'error' });
    }
  }, [currentUser?.uid, enqueueSnackbar]);

  const loadPlacements = useCallback(async (sceneId) => {
    if (!sceneId) {
      setPlacements([]);
      return;
    }
    try {
      let snap;
      try {
        snap = await getDocs(query(
          collection(db, 'geoScenes', sceneId, 'placements'),
          orderBy('createdAt', 'desc')
        ));
      } catch {
        snap = await getDocs(collection(db, 'geoScenes', sceneId, 'placements'));
      }
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      list.sort((a, b) => {
        const ao = Number(a.routeOrder ?? 9999);
        const bo = Number(b.routeOrder ?? 9999);
        if (ao !== bo) return ao - bo;
        return String(a.title || '').localeCompare(String(b.title || ''));
      });
      setPlacements(list);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudieron cargar las ubicaciones de modelos', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await Promise.all([loadScenes(), loadModels()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [currentUser?.uid, loadModels, loadScenes]);

  useEffect(() => {
    loadPlacements(sceneForm.id);
  }, [sceneForm.id, loadPlacements]);

  const resetScene = () => {
    setCreatingNewScene(true);
    setSceneForm(emptySceneForm);
    setSceneFormOpen(true);
    setPlacements([]);
    setPlacementForm(emptyPlacementForm);
    setPlacementStep(1);
    setLocationTab('map');
  };

  const selectScene = (scene) => {
    setCreatingNewScene(false);
    setSceneFormOpen(false);
    setSceneForm({
      id: scene.id,
      name: scene.name || '',
      description: scene.description || '',
      location: scene.location || '',
      isPublic: !!scene.isPublic,
      center: Array.isArray(scene.center) ? scene.center : null,
      activationRadiusMeters: Number(scene.activationRadiusMeters) || 120,
      autoOpenOnEnter: !!scene.autoOpenOnEnter,
      routeGuided: !!scene.routeGuided,
      routeAutoAdvance: typeof scene.routeAutoAdvance === 'boolean' ? scene.routeAutoAdvance : true
    });
    setPlacementForm(emptyPlacementForm);
    setPlacementStep(1);
    setLocationTab('map');
  };

  const handleSaveScene = async () => {
    if (!sceneForm.name.trim()) {
      enqueueSnackbar('El nombre de la escena es obligatorio', { variant: 'warning' });
      return;
    }
    if (!sceneForm.center || sceneForm.center.length !== 2) {
      enqueueSnackbar('Selecciona una coordenada central en el mapa', { variant: 'warning' });
      return;
    }

    setSavingScene(true);
    try {
      const nowISO = new Date().toISOString();
      const payload = {
        name: sceneForm.name.trim(),
        description: sceneForm.description.trim(),
        location: sceneForm.location.trim(),
        isPublic: !!sceneForm.isPublic,
        center: sceneForm.center,
        activationRadiusMeters: Math.max(10, Number(sceneForm.activationRadiusMeters) || 120),
        autoOpenOnEnter: !!sceneForm.autoOpenOnEnter,
        routeGuided: !!sceneForm.routeGuided,
        routeAutoAdvance: !!sceneForm.routeAutoAdvance,
        userId: currentUser?.uid || 'anonymous',
        createdAt: sceneForm.id ? undefined : nowISO,
        lastUpdated: nowISO
      };

      if (sceneForm.id) {
        const cp = { ...payload };
        delete cp.createdAt;
        await updateDoc(doc(db, 'geoScenes', sceneForm.id), cp);
        enqueueSnackbar('Escena geográfica actualizada', { variant: 'success' });
        setSceneFormOpen(false);
      } else {
        const ref = await addDoc(collection(db, 'geoScenes'), payload);
        enqueueSnackbar('Escena geográfica creada. Ahora agregá modelos abajo.', { variant: 'success' });
        setCreatingNewScene(false);
        setSceneFormOpen(false);
        setSceneForm((prev) => ({ ...prev, id: ref.id }));
      }
      await loadScenes();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo guardar la escena', { variant: 'error' });
    } finally {
      setSavingScene(false);
    }
  };

  const handleDeleteScene = async (sceneId) => {
    if (!sceneId) return;
    if (!window.confirm('¿Eliminar esta escena y todos sus modelos ubicados?')) return;
    try {
      const snap = await getDocs(collection(db, 'geoScenes', sceneId, 'placements'));
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      await deleteDoc(doc(db, 'geoScenes', sceneId));
      enqueueSnackbar('Escena eliminada', { variant: 'success' });
      if (sceneForm.id === sceneId) resetScene();
      await loadScenes();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo eliminar la escena', { variant: 'error' });
    }
  };

  const handleSavePlacement = async () => {
    if (!sceneForm.id) {
      enqueueSnackbar('Primero guarda la escena geográfica', { variant: 'warning' });
      return;
    }
    if (!placementForm.modelId) {
      enqueueSnackbar('Selecciona un modelo', { variant: 'warning' });
      return;
    }
    const lat = Number(placementForm.lat);
    const lng = Number(placementForm.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      enqueueSnackbar('Latitud y longitud deben ser valores numéricos válidos', { variant: 'warning' });
      return;
    }

    setSavingPlacement(true);
    try {
      const nowISO = new Date().toISOString();
      const nextScaleValue = Number(placementForm.scale) || 1;
      const nextRotationValue = Number(placementForm.rotationY) || 0;
      const persistedTransform = sanitizeArTransform(placementForm.arTransform)
        || buildFallbackTransform(nextScaleValue, nextRotationValue);
      const payload = {
        modelId: selectedModel?.id || '',
        modelName: selectedModel?.name || 'Modelo',
        modelUrl: selectedModel?.modelUrl || '',
        thumbnailUrl: selectedModel?.thumbnailUrl || '',
        artist: selectedModel?.artist || '',
        title: placementForm.title.trim() || selectedModel?.name || 'Modelo',
        description: placementForm.description.trim(),
        lat,
        lng,
        scale: nextScaleValue,
        rotationY: nextRotationValue,
        arTransform: persistedTransform,
        activationRadiusMeters: Math.max(5, Number(placementForm.activationRadiusMeters) || 25),
        routeOrder: Math.max(1, Number(placementForm.routeOrder) || (placements.length + 1)),
        createdAt: placementForm.id ? undefined : nowISO,
        lastUpdated: nowISO
      };

      if (placementForm.id) {
        const cp = { ...payload };
        delete cp.createdAt;
        await updateDoc(doc(db, 'geoScenes', sceneForm.id, 'placements', placementForm.id), cp);
      } else {
        await addDoc(collection(db, 'geoScenes', sceneForm.id, 'placements'), payload);
      }
      enqueueSnackbar('Ubicación del modelo guardada', { variant: 'success' });
      setPlacementForm({
        ...emptyPlacementForm,
        lat: sceneForm.center?.[0] ?? '',
        lng: sceneForm.center?.[1] ?? '',
        routeOrder: placements.length + 1
      });
      setPlacementStep(1);
      setLocationTab('map');
      await loadPlacements(sceneForm.id);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo guardar la ubicación del modelo', { variant: 'error' });
    } finally {
      setSavingPlacement(false);
    }
  };

  const handleDeletePlacement = async (placementId) => {
    if (!placementId || !sceneForm.id) return;
    if (!window.confirm('¿Eliminar esta ubicación de modelo?')) return;
    try {
      await deleteDoc(doc(db, 'geoScenes', sceneForm.id, 'placements', placementId));
      enqueueSnackbar('Ubicación eliminada', { variant: 'success' });
      await loadPlacements(sceneForm.id);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo eliminar la ubicación', { variant: 'error' });
    }
  };

  const handleEditPlacement = (placement) => {
    setPlacementForm({
      id: placement.id,
      modelId: placement.modelId || '',
      title: placement.title || '',
      description: placement.description || '',
      lat: placement.lat ?? '',
      lng: placement.lng ?? '',
      scale: placement.scale ?? 1,
      rotationY: placement.rotationY ?? 0,
      arTransform: placement.arTransform || null,
      activationRadiusMeters: placement.activationRadiusMeters ?? 25,
      routeOrder: placement.routeOrder ?? 1
    });
    setGpsPointInfo(null);
    setGpsCaptureStats(null);
    setPlacementStep(1);
    setLocationTab('map');
  };

  const anchorFromCurrentLocation = () => {
    if (!navigator.geolocation) {
      enqueueSnackbar('Este dispositivo/navegador no soporta geolocalización.', { variant: 'warning' });
      return;
    }
    setCapturingGpsPoint(true);
    setGpsPointInfo(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude);
        const lng = Number(pos.coords.longitude);
        const accuracy = Number(pos.coords.accuracy || 0);
        setPlacementForm((p) => ({ ...p, lat, lng }));
        setGpsPointInfo({
          accuracy,
          capturedAt: new Date()
        });
        if (accuracy > 25) {
          enqueueSnackbar(`Punto capturado, pero precisión baja (±${Math.round(accuracy)} m). Repite la captura parado unos segundos.`, { variant: 'warning' });
        } else {
          enqueueSnackbar(`Punto GPS capturado con precisión ±${Math.round(accuracy)} m`, { variant: 'success' });
        }
        setCapturingGpsPoint(false);
      },
      (err) => {
        console.error(err);
        enqueueSnackbar(describeGeoError(err), { variant: 'error' });
        setCapturingGpsPoint(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const clearPreciseCaptureRuntime = () => {
    if (gpsWatchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }
    if (gpsTimerRef.current != null) {
      clearInterval(gpsTimerRef.current);
      gpsTimerRef.current = null;
    }
  };

  const stopPreciseCapture = (finalize = true) => {
    clearPreciseCaptureRuntime();
    setCapturingGpsPoint(false);

    if (!finalize) {
      setGpsCaptureStats(null);
      return;
    }

    const samples = gpsSamplesRef.current
      .filter((s) => Number.isFinite(s.accuracy) && s.accuracy > 0)
      .sort((a, b) => a.accuracy - b.accuracy);
    if (samples.length === 0) {
      enqueueSnackbar('No se obtuvieron muestras GPS válidas.', { variant: 'error' });
      setGpsCaptureStats(null);
      return;
    }

    const goodSamples = samples.filter((s) => s.accuracy <= PRECISE_GOOD_ACCURACY_METERS);
    const useSamples = goodSamples.length >= 4 ? goodSamples : samples.slice(0, Math.min(samples.length, 10));

    let sumW = 0;
    let sumLat = 0;
    let sumLng = 0;
    useSamples.forEach((s) => {
      const w = 1 / (s.accuracy * s.accuracy);
      sumW += w;
      sumLat += s.lat * w;
      sumLng += s.lng * w;
    });
    const lat = sumLat / sumW;
    const lng = sumLng / sumW;
    const estimatedAccuracy = Math.sqrt(1 / sumW);
    const bestAccuracy = samples[0].accuracy;

    setPlacementForm((p) => ({ ...p, lat, lng }));
    setGpsPointInfo({
      accuracy: estimatedAccuracy,
      bestAccuracy,
      sampleCount: samples.length,
      usedSampleCount: useSamples.length,
      method: 'precise',
      capturedAt: new Date()
    });
    setGpsCaptureStats(null);

    if (estimatedAccuracy <= PRECISE_TARGET_ACCURACY_METERS) {
      enqueueSnackbar(`Captura precisa completada (±${Math.round(estimatedAccuracy)} m).`, { variant: 'success' });
    } else {
      enqueueSnackbar(`Captura completada, precisión ±${Math.round(estimatedAccuracy)} m. Recomendado repetir para acercarte a ±${PRECISE_TARGET_ACCURACY_METERS} m.`, { variant: 'warning' });
    }
  };

  const startPreciseCapture = () => {
    if (!navigator.geolocation) {
      enqueueSnackbar('Este dispositivo/navegador no soporta geolocalización.', { variant: 'warning' });
      return;
    }
    if (capturingGpsPoint) return;

    setCapturingGpsPoint(true);
    setGpsPointInfo(null);
    gpsSamplesRef.current = [];
    gpsCaptureStartRef.current = Date.now();
    setGpsCaptureStats({
      elapsedSec: 0,
      totalSamples: 0,
      goodSamples: 0,
      bestAccuracy: null
    });

    gpsWatchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude);
        const lng = Number(pos.coords.longitude);
        const accuracy = Number(pos.coords.accuracy || Infinity);
        const t = Date.now();
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(accuracy)) return;
        gpsSamplesRef.current.push({ lat, lng, accuracy, t });
        const best = gpsSamplesRef.current.reduce((m, s) => Math.min(m, s.accuracy), Infinity);
        const good = gpsSamplesRef.current.filter((s) => s.accuracy <= PRECISE_GOOD_ACCURACY_METERS).length;
        setGpsCaptureStats({
          elapsedSec: Math.floor((t - gpsCaptureStartRef.current) / 1000),
          totalSamples: gpsSamplesRef.current.length,
          goodSamples: good,
          bestAccuracy: Number.isFinite(best) ? best : null
        });
      },
      (err) => {
        console.error(err);
        enqueueSnackbar(describeGeoError(err), { variant: 'error' });
        stopPreciseCapture(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );

    gpsTimerRef.current = setInterval(() => {
      const elapsedSec = Math.floor((Date.now() - gpsCaptureStartRef.current) / 1000);
      setGpsCaptureStats((prev) => prev ? { ...prev, elapsedSec } : prev);
      if (elapsedSec >= PRECISE_CAPTURE_SECONDS) {
        stopPreciseCapture(true);
      }
    }, 1000);
  };

  useEffect(() => () => clearPreciseCaptureRuntime(), []);

  const openPlacementCalibration = () => {
    if (!selectedModel?.modelUrl) {
      enqueueSnackbar('Primero selecciona un modelo para calibrar.', { variant: 'warning' });
      return;
    }
    const existing = sanitizeArTransform(placementForm.arTransform);
    const fallbackScale = Number(placementForm.scale);
    const fallbackRotation = Number(placementForm.rotationY);
    const fallbackTransform = {
      orientation: `0deg ${Number.isFinite(fallbackRotation) ? fallbackRotation : 0}deg 0deg`,
      scale: `${Number.isFinite(fallbackScale) && fallbackScale > 0 ? fallbackScale : 1} ${Number.isFinite(fallbackScale) && fallbackScale > 0 ? fallbackScale : 1} ${Number.isFinite(fallbackScale) && fallbackScale > 0 ? fallbackScale : 1}`
    };
    setCalibrationTransform(existing || fallbackTransform);
    setCalibrationScale(Number.isFinite(fallbackScale) && fallbackScale > 0 ? fallbackScale : 1);
    setCalibrationRotationY(Number.isFinite(fallbackRotation) ? fallbackRotation : 0);
    setCalibrationArStatus('idle');
    setCalibrationSaveState('idle');
    setCalibrationSaveErrorMessage('');
    setCalibrationOpen(true);
  };

  const savePlacementCalibration = async () => {
    setCalibrationSaveState('idle');
    setCalibrationSaveErrorMessage('');
    const safeTransform = sanitizeArTransform(calibrationTransform);
    const nextScale = parseUniformScale(safeTransform?.scale);
    const nextRotationY = parseYawFromOrientation(safeTransform?.orientation);
    const manualScale = Number(calibrationScale);
    const manualRotation = Number(calibrationRotationY);
    const nextScaleValue = Number.isFinite(manualScale) && manualScale > 0
      ? Number(manualScale.toFixed(4))
      : (Number.isFinite(nextScale) && nextScale > 0 ? Number(nextScale.toFixed(4)) : Number(placementForm.scale) || 1);
    const nextRotationYValue = Number.isFinite(manualRotation)
      ? Number(manualRotation.toFixed(1))
      : (Number.isFinite(nextRotationY) ? Number(nextRotationY.toFixed(1)) : Number(placementForm.rotationY) || 0);
    const transformToPersist = safeTransform
      || sanitizeArTransform(placementForm.arTransform)
      || buildFallbackTransform(nextScaleValue, nextRotationYValue);

    setPlacementForm((prev) => ({
      ...prev,
      arTransform: transformToPersist,
      scale: nextScaleValue,
      rotationY: nextRotationYValue
    }));

    if (sceneForm.id && placementForm.id) {
      try {
        setCalibrationSaveState('saving');
        await updateDoc(doc(db, 'geoScenes', sceneForm.id, 'placements', placementForm.id), {
          arTransform: transformToPersist,
          scale: nextScaleValue,
          rotationY: nextRotationYValue,
          lastUpdated: new Date().toISOString()
        });
        setCalibrationSaveState('saved');
        enqueueSnackbar('Calibración AR guardada en el punto.', { variant: 'success' });
        await loadPlacements(sceneForm.id);
      } catch (e) {
        console.error(e);
        setCalibrationSaveState('error');
        setCalibrationSaveErrorMessage(e?.message || 'Error al actualizar el punto existente.');
        enqueueSnackbar('No se pudo guardar la calibración AR.', { variant: 'error' });
        return;
      }
    } else {
      if (!sceneForm.id || !placementForm.modelId) {
        setCalibrationSaveState('error');
        setCalibrationSaveErrorMessage('Falta escena guardada o modelo seleccionado.');
        enqueueSnackbar('Faltan datos base del punto (escena/modelo) para persistir la calibración.', { variant: 'warning' });
        return;
      }
      let lat = Number(placementForm.lat);
      let lng = Number(placementForm.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        const fallbackLat = Number(sceneForm.center?.[0]);
        const fallbackLng = Number(sceneForm.center?.[1]);
        if (Number.isFinite(fallbackLat) && Number.isFinite(fallbackLng)) {
          lat = fallbackLat;
          lng = fallbackLng;
          enqueueSnackbar('No había lat/lng del punto. Se usó el centro de la escena como fallback.', { variant: 'info' });
        } else {
          setCalibrationSaveState('error');
          setCalibrationSaveErrorMessage('Falta lat/lng y no hay centro de escena para fallback.');
          enqueueSnackbar('Faltan latitud/longitud del punto para persistir la calibración.', { variant: 'warning' });
          return;
        }
      }
      try {
        setCalibrationSaveState('saving');
        const nowISO = new Date().toISOString();
        const payload = {
          modelId: selectedModel?.id || placementForm.modelId || '',
          modelName: selectedModel?.name || 'Modelo',
          modelUrl: selectedModel?.modelUrl || '',
          thumbnailUrl: selectedModel?.thumbnailUrl || '',
          artist: selectedModel?.artist || '',
          title: placementForm.title.trim() || selectedModel?.name || 'Modelo',
          description: placementForm.description.trim(),
          lat,
          lng,
          scale: nextScaleValue,
          rotationY: nextRotationYValue,
          arTransform: transformToPersist,
          activationRadiusMeters: Math.max(5, Number(placementForm.activationRadiusMeters) || 25),
          routeOrder: Math.max(1, Number(placementForm.routeOrder) || (placements.length + 1)),
          createdAt: nowISO,
          lastUpdated: nowISO
        };
        const ref = await addDoc(collection(db, 'geoScenes', sceneForm.id, 'placements'), payload);
        setPlacementForm((prev) => ({ ...prev, id: ref.id, lat, lng }));
        setCalibrationSaveState('saved');
        enqueueSnackbar('Calibración AR guardada y punto creado.', { variant: 'success' });
        await loadPlacements(sceneForm.id);
      } catch (e) {
        console.error(e);
        setCalibrationSaveState('error');
        setCalibrationSaveErrorMessage(e?.message || 'Error al crear el punto calibrado.');
        enqueueSnackbar('No se pudo crear el punto con calibración AR.', { variant: 'error' });
        return;
      }
    }
    setCalibrationOpen(false);
  };

  const handleCalibrationTransformChange = useCallback((transform) => {
    const safe = sanitizeArTransform(transform);
    if (!safe) return;
    setCalibrationTransform(safe);
    const s = parseUniformScale(safe.scale);
    if (Number.isFinite(s) && s > 0) {
      setCalibrationScale(Number(s.toFixed(4)));
    }
    const r = parseYawFromOrientation(safe.orientation);
    if (Number.isFinite(r)) {
      setCalibrationRotationY(Number(r.toFixed(1)));
    }
  }, []);

  if (loading) {
    return <div className="geo-admin-empty">Cargando módulo geográfico...</div>;
  }

  const calibrationSummaryScale = Number.isFinite(Number(calibrationScale))
    ? Number(calibrationScale)
    : (Number.isFinite(parseUniformScale(calibrationTransform?.scale)) ? Number(parseUniformScale(calibrationTransform?.scale)) : (Number(placementForm.scale) || 1));
  const calibrationSummaryRotationY = Number.isFinite(Number(calibrationRotationY))
    ? Number(calibrationRotationY)
    : (Number.isFinite(parseYawFromOrientation(calibrationTransform?.orientation)) ? Number(parseYawFromOrientation(calibrationTransform?.orientation)) : (Number(placementForm.rotationY) || 0));

  const showSceneForm = !sceneForm.id || sceneFormOpen;
  const placementSteps = [
    { n: 1, label: 'Modelo' },
    { n: 2, label: 'Ubicación' },
    { n: 3, label: 'Ajustes y guardar' }
  ];
  const canGoToStep2 = !!placementForm.modelId;
  const canGoToStep3 = canGoToStep2 && placementForm.lat !== '' && placementForm.lng !== '';

  return (
    <div className="geo-admin-wrap">
      <header className="geo-admin-header">
        <div>
          <h2>Escenas Geográficas</h2>
          <p>Define un parque/sitio y posiciona múltiples modelos GLB en coordenadas reales.</p>
        </div>
        <button className="geo-btn geo-btn-secondary" onClick={resetScene}>Nueva escena</button>
      </header>

      <div className="geo-admin-grid">
        <aside className="geo-card">
          <h3>Mis escenas</h3>
          {scenes.length === 0 ? (
            <p className="geo-muted">Aún no tenés escenas creadas.</p>
          ) : (
            <div className="geo-scene-list">
              {scenes.map((scene) => (
                <div
                  key={scene.id}
                  className={`geo-scene-item ${sceneForm.id === scene.id ? 'active' : ''}`}
                >
                    <button
                      className="geo-scene-item-main"
                      onClick={() => selectScene(scene)}
                  >
                    <strong>{scene.name || 'Sin nombre'}</strong>
                    <span>{scene.location || 'Sin ubicación'}</span>
                  </button>
                  <button
                    className="geo-icon-btn danger"
                    onClick={() => handleDeleteScene(scene.id)}
                    title="Eliminar escena"
                  >
                    <i className="bi bi-trash" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </aside>

        <section className="geo-card">
          {!showSceneForm ? (
            <div className="geo-scene-summary">
              <div className="geo-scene-summary-head">
                <span className="geo-eyebrow">Escena activa</span>
                <h3>{sceneForm.name || 'Sin nombre'}</h3>
                <p className="geo-muted">{sceneForm.location || 'Sin ubicación de referencia'}</p>
              </div>
              <div className="geo-scene-summary-stats">
                <div>
                  <strong>{placements.length}</strong>
                  <span>modelo{placements.length === 1 ? '' : 's'} ubicado{placements.length === 1 ? '' : 's'}</span>
                </div>
                <div>
                  <strong>{sceneForm.activationRadiusMeters}m</strong>
                  <span>radio de activación</span>
                </div>
                <div>
                  <strong>{sceneForm.isPublic ? 'Pública' : 'Oculta'}</strong>
                  <span>visibilidad</span>
                </div>
              </div>
              <button className="geo-btn geo-btn-secondary" onClick={() => setSceneFormOpen(true)}>
                <i className="bi bi-pencil" /> Editar datos de la escena
              </button>
            </div>
          ) : (
          <>
          <h3>{sceneForm.id ? 'Editar escena' : 'Nueva escena'}</h3>
          <div className="geo-form-grid">
            <label>
              Nombre de escena
              <input
                value={sceneForm.name}
                onChange={(e) => setSceneForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Ej: Parque de las Esculturas"
              />
            </label>
            <label>
              Ubicación / referencia
              <input
                value={sceneForm.location}
                onChange={(e) => setSceneForm((p) => ({ ...p, location: e.target.value }))}
                placeholder="Ej: La Rioja, Argentina"
              />
            </label>
          </div>

          <label className="geo-block">
            Descripción
            <textarea
              rows={3}
              value={sceneForm.description}
              onChange={(e) => setSceneForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Contexto de la escena, recorrido o curaduría."
            />
          </label>

          <label className="geo-check">
            <input
              type="checkbox"
              checked={sceneForm.isPublic}
              onChange={(e) => setSceneForm((p) => ({ ...p, isPublic: e.target.checked }))}
            />
            Escena pública (visible en la web)
          </label>

          <div className="geo-form-grid">
            <label>
              Radio de activación (metros)
              <input
                type="number"
                min="10"
                step="5"
                value={sceneForm.activationRadiusMeters}
                onChange={(e) => setSceneForm((p) => ({ ...p, activationRadiusMeters: e.target.value }))}
              />
            </label>
            <label className="geo-check">
              <input
                type="checkbox"
                checked={sceneForm.autoOpenOnEnter}
                onChange={(e) => setSceneForm((p) => ({ ...p, autoOpenOnEnter: e.target.checked }))}
              />
              Autoabrir escena al entrar al radio
            </label>
          </div>

          <div className="geo-form-grid">
            <label className="geo-check">
              <input
                type="checkbox"
                checked={sceneForm.routeGuided}
                onChange={(e) => setSceneForm((p) => ({ ...p, routeGuided: e.target.checked }))}
              />
              Activar ruta guiada por orden de puntos
            </label>
            <label className="geo-check">
              <input
                type="checkbox"
                checked={sceneForm.routeAutoAdvance}
                onChange={(e) => setSceneForm((p) => ({ ...p, routeAutoAdvance: e.target.checked }))}
              />
              Auto avanzar al siguiente punto
            </label>
          </div>

          <div className="geo-block">
            <div className="geo-block-title">Centro geográfico de la escena (click en el mapa)</div>
            <GeoPointPicker
              value={sceneForm.center}
              onPick={({ lat, lng }) => setSceneForm((p) => ({ ...p, center: [lat, lng] }))}
            />
            {sceneForm.center && (
              <p className="geo-muted">
                Lat: {sceneForm.center[0].toFixed(6)} | Lng: {sceneForm.center[1].toFixed(6)}
              </p>
            )}
          </div>

          <div className="geo-actions">
            <button className="geo-btn geo-btn-primary" disabled={savingScene} onClick={handleSaveScene}>
              {savingScene ? 'Guardando...' : 'Guardar escena'}
            </button>
            {sceneForm.id && (
              <button className="geo-btn geo-btn-secondary" onClick={() => setSceneFormOpen(false)}>
                Cancelar
              </button>
            )}
          </div>
          </>
          )}
        </section>
      </div>

      <section className="geo-card">
        <h3>Modelos ubicados en la escena</h3>
        {!sceneForm.id ? (
          <p className="geo-muted">Guarda la escena primero para poder agregar modelos geolocalizados.</p>
        ) : (
          <>
            {placementForm.id && (
              <div className="geo-editing-banner">
                <i className="bi bi-pencil-square" /> Editando: <strong>{placementForm.title || selectedModel?.name || 'punto'}</strong>
                <button
                  type="button"
                  className="geo-link-btn"
                  onClick={() => {
                    setPlacementForm({
                      ...emptyPlacementForm,
                      lat: sceneForm.center?.[0] ?? '',
                      lng: sceneForm.center?.[1] ?? ''
                    });
                    setPlacementStep(1);
                  }}
                >
                  Cancelar edición
                </button>
              </div>
            )}

            <div className="geo-wizard-steps">
              {placementSteps.map((step) => {
                const reachable = step.n === 1 || (step.n === 2 && canGoToStep2) || (step.n === 3 && canGoToStep3);
                return (
                  <button
                    key={step.n}
                    type="button"
                    className={`geo-step ${placementStep === step.n ? 'active' : ''} ${step.n < placementStep ? 'done' : ''}`}
                    onClick={() => reachable && setPlacementStep(step.n)}
                    disabled={!reachable}
                  >
                    <span className="geo-step-num">{step.n < placementStep ? <i className="bi bi-check-lg" /> : step.n}</span>
                    {step.label}
                  </button>
                );
              })}
            </div>

            {placementStep === 1 && (
              <div className="geo-step-panel">
                <label className="geo-block">
                  Modelo a ubicar
                  <select
                    value={placementForm.modelId}
                    onChange={(e) => setPlacementForm((p) => ({ ...p, modelId: e.target.value }))}
                  >
                    <option value="">Seleccionar modelo</option>
                    {models.map((m) => (
                      <option key={m.id} value={m.id}>{m.name || m.id}</option>
                    ))}
                  </select>
                </label>

                {selectedModel && (
                  <div className="geo-model-preview">
                    <div className="geo-model-preview-thumb">
                      {selectedModel.thumbnailUrl ? (
                        <img src={selectedModel.thumbnailUrl} alt={selectedModel.name} />
                      ) : (
                        <i className="bi bi-box-seam" />
                      )}
                    </div>
                    <div>
                      <strong>{selectedModel.name || 'Modelo'}</strong>
                      <span>{selectedModel.artist ? `${selectedModel.artist} · ` : ''}{selectedModel.category || 'Sin categoría'}</span>
                    </div>
                  </div>
                )}

                <label className="geo-block">
                  Título en mapa
                  <input
                    value={placementForm.title}
                    onChange={(e) => setPlacementForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Ej: Escultura Norte"
                  />
                </label>

                <div className="geo-actions">
                  <button
                    type="button"
                    className="geo-btn geo-btn-primary"
                    disabled={!canGoToStep2}
                    onClick={() => setPlacementStep(2)}
                  >
                    Siguiente: Ubicación <i className="bi bi-arrow-right" />
                  </button>
                </div>
              </div>
            )}

            {placementStep === 2 && (
              <div className="geo-step-panel">
                <div className="geo-tabs">
                  <button type="button" className={`geo-tab ${locationTab === 'map' ? 'active' : ''}`} onClick={() => setLocationTab('map')}>
                    <i className="bi bi-map" /> Mapa
                  </button>
                  <button type="button" className={`geo-tab ${locationTab === 'streetview' ? 'active' : ''}`} onClick={() => setLocationTab('streetview')}>
                    <i className="bi bi-signpost-2" /> Street View
                  </button>
                  <button type="button" className={`geo-tab ${locationTab === 'gps' ? 'active' : ''}`} onClick={() => setLocationTab('gps')}>
                    <i className="bi bi-geo-alt" /> GPS en sitio
                  </button>
                </div>

                {locationTab === 'map' && (
                  <div className="geo-tab-panel">
                    <p className="geo-muted">Hacé click en el mapa para marcar el punto exacto del modelo.</p>
                    <GeoPointPicker
                      value={
                        placementForm.lat !== '' && placementForm.lng !== ''
                          ? [Number(placementForm.lat), Number(placementForm.lng)]
                          : sceneForm.center
                      }
                      onPick={({ lat, lng }) => setPlacementForm((p) => ({ ...p, lat, lng }))}
                    />
                  </div>
                )}

                {locationTab === 'streetview' && (
                  <div className="geo-tab-panel">
                    <p className="geo-muted">Navegá por Street View y soltá en la posición exacta.</p>
                    <StreetViewPicker
                      apiKey={GOOGLE_MAPS_API_KEY}
                      value={
                        placementForm.lat !== '' && placementForm.lng !== ''
                          ? [Number(placementForm.lat), Number(placementForm.lng)]
                          : sceneForm.center
                      }
                      onPick={({ lat, lng }) => setPlacementForm((p) => ({ ...p, lat, lng }))}
                    />
                  </div>
                )}

                {locationTab === 'gps' && (
                  <div className="geo-tab-panel">
                    <p className="geo-muted">Parate en el lugar exacto con tu dispositivo y capturá la posición por GPS.</p>
                    <div className="geo-gps-options">
                      <button
                        type="button"
                        className="geo-btn geo-btn-secondary"
                        onClick={anchorFromCurrentLocation}
                        disabled={capturingGpsPoint}
                      >
                        <i className="bi bi-crosshair" /> {capturingGpsPoint ? 'Capturando GPS...' : 'Captura rápida'}
                      </button>
                      <button
                        type="button"
                        className="geo-btn geo-btn-secondary"
                        onClick={capturingGpsPoint ? () => stopPreciseCapture(true) : startPreciseCapture}
                      >
                        <i className="bi bi-stopwatch" /> {capturingGpsPoint ? 'Detener captura precisa' : 'Captura precisa (30s)'}
                      </button>
                    </div>
                    {gpsCaptureStats && (
                      <p className="geo-muted">
                        Captura precisa en curso: {gpsCaptureStats.elapsedSec}s/{PRECISE_CAPTURE_SECONDS}s · muestras {gpsCaptureStats.totalSamples}
                        {' · '}buenas ({`≤${PRECISE_GOOD_ACCURACY_METERS}m`}): {gpsCaptureStats.goodSamples}
                        {gpsCaptureStats.bestAccuracy ? ` · mejor ±${Math.round(gpsCaptureStats.bestAccuracy)} m` : ''}
                      </p>
                    )}
                    {gpsPointInfo && (
                      <p className="geo-muted">
                        Captura GPS ({gpsPointInfo.method === 'precise' ? 'precisa' : 'rápida'}): ±{Math.round(gpsPointInfo.accuracy)} m
                        {gpsPointInfo.bestAccuracy ? ` · mejor muestra ±${Math.round(gpsPointInfo.bestAccuracy)} m` : ''}
                        {gpsPointInfo.sampleCount ? ` · muestras ${gpsPointInfo.usedSampleCount || gpsPointInfo.sampleCount}/${gpsPointInfo.sampleCount}` : ''}
                        {' · '}
                        {gpsPointInfo.capturedAt.toLocaleTimeString('es-ES')}
                      </p>
                    )}
                  </div>
                )}

                {placementForm.lat !== '' && placementForm.lng !== '' ? (
                  <p className="geo-point-readout">
                    <i className="bi bi-geo-alt-fill" /> Punto seleccionado: {Number(placementForm.lat).toFixed(6)}, {Number(placementForm.lng).toFixed(6)}
                  </p>
                ) : (
                  <p className="geo-muted">Todavía no elegiste un punto para este modelo.</p>
                )}

                <div className="geo-actions">
                  <button type="button" className="geo-btn geo-btn-secondary" onClick={() => setPlacementStep(1)}>
                    <i className="bi bi-arrow-left" /> Atrás
                  </button>
                  <button
                    type="button"
                    className="geo-btn geo-btn-primary"
                    disabled={!canGoToStep3}
                    onClick={() => setPlacementStep(3)}
                  >
                    Siguiente: Ajustes <i className="bi bi-arrow-right" />
                  </button>
                </div>
              </div>
            )}

            {placementStep === 3 && (
              <div className="geo-step-panel">
                <div className="geo-placement-grid">
                  <label>
                    Escala inicial
                    <input
                      type="number"
                      step="0.1"
                      value={placementForm.scale}
                      onChange={(e) => setPlacementForm((p) => ({ ...p, scale: e.target.value }))}
                    />
                  </label>
                  <label>
                    Rotación Y inicial
                    <input
                      type="number"
                      step="1"
                      value={placementForm.rotationY}
                      onChange={(e) => setPlacementForm((p) => ({ ...p, rotationY: e.target.value }))}
                    />
                  </label>
                  <label>
                    Radio inmersivo (m)
                    <input
                      type="number"
                      min="5"
                      step="1"
                      value={placementForm.activationRadiusMeters}
                      onChange={(e) => setPlacementForm((p) => ({ ...p, activationRadiusMeters: e.target.value }))}
                    />
                  </label>
                  <label>
                    Orden en ruta
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={placementForm.routeOrder}
                      onChange={(e) => setPlacementForm((p) => ({ ...p, routeOrder: e.target.value }))}
                    />
                  </label>
                </div>

                <label className="geo-block">
                  Descripción (opcional)
                  <textarea
                    rows={2}
                    value={placementForm.description}
                    onChange={(e) => setPlacementForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </label>

                <div className="geo-block">
                  <button
                    type="button"
                    className="geo-btn geo-btn-secondary"
                    onClick={openPlacementCalibration}
                    disabled={!selectedModel?.modelUrl}
                  >
                    <i className="bi bi-camera-reels" /> Calibrar en sitio (AR) — opcional
                  </button>
                  {placementForm.arTransform && (
                    <p className="geo-muted">
                      Calibración AR lista: escala {parseUniformScale(placementForm.arTransform.scale)?.toFixed(3) || 'N/A'}
                      {' · '}rotación Y {parseYawFromOrientation(placementForm.arTransform.orientation)?.toFixed(1) || 'N/A'}°
                    </p>
                  )}
                </div>

                <div className="geo-actions">
                  <button type="button" className="geo-btn geo-btn-secondary" onClick={() => setPlacementStep(2)}>
                    <i className="bi bi-arrow-left" /> Atrás
                  </button>
                  <button className="geo-btn geo-btn-primary" disabled={savingPlacement} onClick={handleSavePlacement}>
                    {savingPlacement ? 'Guardando...' : (placementForm.id ? 'Actualizar punto' : 'Agregar modelo al mapa')}
                  </button>
                </div>
              </div>
            )}

            <div className="geo-placement-list-header">
              <h4>Modelos ya ubicados en esta escena</h4>
              <span className="geo-muted">{placements.length}</span>
            </div>
            <div className="geo-placement-list">
              {placements.length === 0 ? (
                <p className="geo-muted">Todavía no hay modelos geolocalizados en esta escena.</p>
              ) : (
                placements.map((p) => (
                  <article key={p.id} className="geo-placement-item">
                    <div>
                      <strong>{p.title || p.modelName || 'Modelo'}</strong>
                      <p>
                        #{Math.max(1, Number(p.routeOrder) || 1)} · {p.modelName || 'Sin modelo'} · {Number(p.lat).toFixed(6)}, {Number(p.lng).toFixed(6)}
                        {' · '}radio {Math.max(5, Number(p.activationRadiusMeters) || 25)}m
                      </p>
                    </div>
                    <div className="geo-placement-actions">
                      <button className="geo-icon-btn" onClick={() => handleEditPlacement(p)} title="Editar">
                        <i className="bi bi-pencil" />
                      </button>
                      <button className="geo-icon-btn danger" onClick={() => handleDeletePlacement(p.id)} title="Eliminar">
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </>
        )}
      </section>

      <Dialog
        open={calibrationOpen}
        onClose={() => setCalibrationOpen(false)}
        fullScreen
        maxWidth="xl"
        PaperProps={{ sx: { backgroundColor: 'black', overflow: 'hidden', height: '100dvh' } }}
      >
        <div className="geo-calibration-toolbar">
          <div>
            <strong>Calibración en sitio (AR)</strong>
            <p>
              Ajusta la escultura en AR (posición, escala y rotación) y luego aplica para guardarla en este punto.
            </p>
            <p>Toca el botón AR (cubo) para entrar al modo inmersivo y mover la escultura.</p>
            <p>
              Estado AR: {calibrationArStatus}
              {` · escala ${calibrationSummaryScale.toFixed(3)}`}
              {` · rotación Y ${calibrationSummaryRotationY.toFixed(1)}°`}
            </p>
            <div className="geo-calibration-tuners">
              <label>
                Escala
                <input
                  type="number"
                  step="0.05"
                  min="0.05"
                  value={calibrationScale}
                  onChange={(e) => setCalibrationScale(e.target.value)}
                />
              </label>
              <label>
                Rotación Y (°)
                <input
                  type="number"
                  step="1"
                  value={calibrationRotationY}
                  onChange={(e) => setCalibrationRotationY(e.target.value)}
                />
              </label>
            </div>
            <p>
              Guardado: {
                calibrationSaveState === 'saving' ? 'guardando...'
                  : calibrationSaveState === 'saved' ? 'ok'
                    : calibrationSaveState === 'error' ? 'error'
                      : 'pendiente'
              }
            </p>
            {calibrationSaveState === 'error' && calibrationSaveErrorMessage && (
              <p>Error: {calibrationSaveErrorMessage}</p>
            )}
          </div>
          <div className="geo-calibration-actions">
            <button className="geo-btn geo-btn-primary" onClick={savePlacementCalibration} disabled={calibrationSaveState === 'saving'}>
              {calibrationSaveState === 'saving' ? 'Guardando...' : 'Aplicar calibración'}
            </button>
            <button className="geo-btn geo-btn-secondary" onClick={() => setCalibrationOpen(false)}>
              Cerrar
            </button>
          </div>
        </div>
        {selectedModel?.modelUrl && (
          <VisorRA
            initialModelUrl={selectedModel.modelUrl}
            isMobile={typeof window !== 'undefined' ? window.innerWidth < 768 : false}
            onClose={() => setCalibrationOpen(false)}
            publicMode
            initialScale={Number(calibrationScale) || Number(placementForm.scale) || 1}
            initialRotationY={Number(calibrationRotationY) || Number(placementForm.rotationY) || 0}
            initialArTransform={calibrationTransform || placementForm.arTransform || null}
            persistModelTransform={false}
            onModelTransformChange={handleCalibrationTransformChange}
            onArStatusChange={(status) => setCalibrationArStatus(status || 'unknown')}
          />
        )}
      </Dialog>
    </div>
  );
}
